
// client/src/components/event/EventAction.jsx
/**
 * =================================================================
 * Event Action Component - UPDATED UI VERSION
 * =================================================================
 * UPDATES:
 * 1. Hero image replaces logo/banner/item-info/media at top
 *    - Tenant provides a single branded image for full top section
 * 2. Dark theme (#1a1a2e) for all content below hero
 * 3. "Powered by On-Screen" branding footer on all pages
 * 4. All existing functionality preserved
 * =================================================================
 */

import React, { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import axios from "axios";
import "./EventAction.css";

const EventAction = () => {
  const { tenant: tenantId, eventId, itemId } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const actionType = queryParams.get("type") || "raffle";

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });

  const [, setTenantLogo] = useState("");
  const [, setEventInfo] = useState(null);
  const [itemInfo, setItemInfo] = useState(null);
  const [itemMedia, setItemMedia] = useState([]);
  const [itemAttributes, setItemAttributes] = useState({});
  const [configData, setConfigData] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    bidamount: "",
    selectedAttributes: {},
    submission_url: "",
    submission_text: "",
    question: "",
    content_url: "",
    content_text: "",
    post_url: "",
    post_text: "",
    platform: "",
    responses: {},
  });

  // AMA Q&A feed state
  const [amaQuestions, setAmaQuestions] = useState([]);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [sortBy, setSortBy] = useState("upvotes"); // upvotes | newest | answered
  const [upvotedIds, setUpvotedIds] = useState(() => {
    try {
      const stored = localStorage.getItem(`ama_upvotes_${tenantId}_${eventId}_${itemId}`);
      return stored ? JSON.parse(stored) : [];
    } catch { return []; }
  });

  const baseUrl = process.env.REACT_APP_BASE_URL || window.location.origin;

  // Debug: Log URL params
  useEffect(() => {
    console.log("[EventAction] URL params:", { tenantId, eventId, itemId, actionType });
    console.log("[EventAction] Base URL:", baseUrl);
  }, [tenantId, eventId, itemId, actionType, baseUrl]);

  // Helper function to convert relative paths to absolute URLs
  const toAbs = (baseUrl, p) =>
    !p ? p : /^https?:\/\//i.test(p) ? p : `${baseUrl}${p.startsWith("/") ? "" : "/"}${p}`;

  // Helper function to check if file is a video
  const isVideo = (f) =>
    (f?.mimetype?.startsWith?.("video/")) ||
    /\.(mp4|webm|mov|m4v|ogg)$/i.test(f?.path || f?.url || f || "");

  // Load all data on component mount and when params change
  useEffect(() => {
    const controller = new AbortController();
    loadData(controller.signal);
    return () => controller.abort();
  }, [tenantId, eventId, itemId, actionType]);

  // Main data loading function - loads all required data in parallel
  const loadData = async (signal) => {
    console.log("[EventAction] Loading data...");
    setLoading(true);
    
    try {
      await Promise.all([
        loadTenantLogo(signal),
        loadEventInfo(signal),
        loadItemInfo(signal),
        loadConfiguration(signal),
      ]);
      console.log("[EventAction] All data loaded successfully");
    } catch (err) {
      if (err.name === 'AbortError') {
        console.log("[EventAction] Request aborted");
        return;
      }
      console.error("[EventAction] Error loading data:", err);
      setMessage({
        text: "Failed to load information. Please try again.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Load tenant logo for branding
  const loadTenantLogo = async (signal) => {
    try {
      console.log("[EventAction] Loading tenant logo...");
      const response = await axios.get(`${baseUrl}/api/tenants/${tenantId}`, { signal });
      if (response.data?.tenant?.logo) {
        setTenantLogo(toAbs(baseUrl, response.data.tenant.logo));
        console.log("[EventAction] Tenant logo loaded");
      }
    } catch (err) {
      if (err.name === 'AbortError') throw err;
      console.error("[EventAction] Error loading tenant logo:", err);
    }
  };

  // Load event information
  const loadEventInfo = async (signal) => {
    try {
      console.log("[EventAction] Loading event info...");
      const response = await axios.get(`${baseUrl}/api/events/${tenantId}/${eventId}`, { signal });
      if (response.data?.event) {
        setEventInfo(response.data.event);
        console.log("[EventAction] Event info loaded:", response.data.event.name);
      }
    } catch (err) {
      if (err.name === 'AbortError') throw err;
      console.error("[EventAction] Error loading event:", err);
    }
  };

  // Load item information, media, and attributes
  const loadItemInfo = async (signal) => {
    try {
      console.log("[EventAction] Loading item info...");
      const [itemRes, imagesRes, attrsRes] = await Promise.all([
        axios.get(`${baseUrl}/api/events/${tenantId}/events/${eventId}/items/${itemId}`, { signal }),
        axios.get(`${baseUrl}/api/events/${tenantId}/events/${eventId}/items/${itemId}/images`, { signal }).catch(() => ({ data: { images: [] } })),
        axios.get(`${baseUrl}/api/events/${tenantId}/events/${eventId}/items/${itemId}/attributes`, { signal }).catch(() => ({ data: {} })),
      ]);

      // Set item info
      if (itemRes.data?.item) {
        setItemInfo(itemRes.data.item);
        console.log("[EventAction] Item info loaded:", itemRes.data.item.name);
      }

      // Process media items
      const images = (imagesRes.data?.images || []);
      const mediaItems = images.map((img) => {
        const path = img.path || img.url || img;
        return {
          url: toAbs(baseUrl, path),
          isVideo: isVideo(img) || isVideo(path),
        };
      });
      setItemMedia(mediaItems);
      console.log("[EventAction] Media items loaded:", mediaItems.length);

      // Process attributes - try endpoint first, then fallback to item
      const attrsFromEndpoint = attrsRes.data?.attributes || attrsRes.data?.rows || attrsRes.data?.data || null;
      const attrsFromItem = itemRes.data?.item?.attributes || null;
      
      const attributesToUse = (Array.isArray(attrsFromEndpoint) && attrsFromEndpoint.length > 0) 
        ? attrsFromEndpoint 
        : attrsFromItem;
      
      if (attributesToUse) {
        const parsed = parseAttributes(attributesToUse);
        setItemAttributes(parsed);
        console.log("[EventAction] Attributes loaded:", Object.keys(parsed));
      }
    } catch (err) {
      if (err.name === 'AbortError') throw err;
      console.error("[EventAction] Error loading item:", err);
    }
  };

  // Action type display names
  const actionTypeLabels = {
    auction: "Auction",
    raffle: "Raffle",
    challenge: "Challenge",
    contest: "Contest",
    live_qna: "Live Q&A",
    ama: "AMA",
    watch_party: "Watch Party",
    workshop: "Workshop",
    ugc_campaign: "UGC Campaign",
    themed_day: "Themed Day",
    collab_takeover: "Collab / Takeover",
    poll_quiz: "Poll / Quiz",
  };

  const actionLabel = actionTypeLabels[actionType] || actionType;

  // Load configuration for any supported action type
  const loadConfiguration = async (signal) => {
    try {
      console.log("[EventAction] Loading configuration...");

      const endpointMap = {
        auction: `${baseUrl}/api/auction/${tenantId}/event/${eventId}/item/${itemId}`,
        raffle: `${baseUrl}/api/raffle/${tenantId}/event/${eventId}/item/${itemId}`,
        challenge: `${baseUrl}/api/challenge/${tenantId}/event/${eventId}/item/${itemId}/config`,
        contest: `${baseUrl}/api/contest/${tenantId}/event/${eventId}/item/${itemId}/config`,
        live_qna: `${baseUrl}/api/live-qna/${tenantId}/event/${eventId}/item/${itemId}/config`,
        ama: `${baseUrl}/api/ama/${tenantId}/event/${eventId}/item/${itemId}/config`,
        watch_party: `${baseUrl}/api/watch-party/${tenantId}/event/${eventId}/item/${itemId}/config`,
        workshop: `${baseUrl}/api/workshop/${tenantId}/event/${eventId}/item/${itemId}/config`,
        ugc_campaign: `${baseUrl}/api/ugc-campaign/${tenantId}/event/${eventId}/item/${itemId}/config`,
        themed_day: `${baseUrl}/api/themed-day/${tenantId}/event/${eventId}/item/${itemId}/config`,
        collab_takeover: `${baseUrl}/api/collab-takeover/${tenantId}/event/${eventId}/item/${itemId}/config`,
        poll_quiz: `${baseUrl}/api/poll-quiz/${tenantId}/event/${eventId}/item/${itemId}/config`,
      };

      const endpoint = endpointMap[actionType] || endpointMap.raffle;
      console.log("[EventAction] Fetching from:", endpoint);
      const response = await axios.get(endpoint, { signal });

      if (response.data.success) {
        const dataKeyMap = {
          auction: "auction",
          raffle: "raffle",
          challenge: "challenge",
          contest: "contest",
          live_qna: "live_qna",
          ama: "ama",
          watch_party: "watch_party",
          workshop: "workshop",
          ugc_campaign: "ugc_campaign",
          themed_day: "themed_day",
          collab_takeover: "collab_takeover",
          poll_quiz: "poll_quiz",
        };
        const data = response.data[dataKeyMap[actionType] || "raffle"];

        if (!data) {
          setMessage({
            text: `No active ${actionLabel} found for this item.`,
            type: "error",
          });
          return;
        }

        // Normalize hasStarted/hasEnded for types that don't have these flags from backend
        if (!("hasStarted" in data) || !("hasEnded" in data)) {
          const now = new Date();
          const scheduledTypes = ["live_qna", "ama", "watch_party", "workshop"];
          if (scheduledTypes.includes(actionType)) {
            // Scheduled types use scheduled_at instead of start_at
            data.hasStarted = data.scheduled_at ? new Date(data.scheduled_at) <= now : (data.status === "ACTIVE" || data.status === "LIVE");
            if (data.scheduled_at && data.duration_minutes) {
              const endTime = new Date(new Date(data.scheduled_at).getTime() + data.duration_minutes * 60000);
              data.hasEnded = endTime <= now;
            } else {
              data.hasEnded = false;
            }
          } else if (actionType === "collab_takeover") {
            // Collab uses scheduled_at + duration_hours
            data.hasStarted = data.scheduled_at ? new Date(data.scheduled_at) <= now : (data.status === "ACTIVE" || data.status === "LIVE");
            if (data.scheduled_at && data.duration_hours) {
              const endTime = new Date(new Date(data.scheduled_at).getTime() + data.duration_hours * 3600000);
              data.hasEnded = endTime <= now;
            } else {
              data.hasEnded = false;
            }
          } else {
            // Competition & campaign types use start_at/end_at
            data.hasStarted = data.start_at ? new Date(data.start_at) <= now : (data.scheduled_at ? new Date(data.scheduled_at) <= now : true);
            data.hasEnded = data.end_at ? new Date(data.end_at) <= now : false;
          }
          if (data.status === "COMPLETE" || data.status === "CANCELLED") {
            data.hasEnded = true;
          }
          // For registration/submission types, allow participation before event starts
          const alwaysOpenTypes = [...scheduledTypes, "ugc_campaign", "themed_day", "poll_quiz", "collab_takeover"];
          if (alwaysOpenTypes.includes(actionType) && data.status !== "COMPLETE" && data.status !== "CANCELLED") {
            data.hasStarted = true;
          }
        }

        setConfigData(data);
        console.log("[EventAction] Configuration loaded:", data);

        // Set starting bid if auction has one
        if (actionType === "auction" && data.starting_bid) {
          setFormData(prev => ({
            ...prev,
            bidamount: data.starting_bid.toString()
          }));
        }

        // Set appropriate messages based on status
        if (data.hasEnded) {
          setMessage({
            text: `This ${actionLabel} has ended.`,
            type: "error",
          });
        } else if (!data.hasStarted) {
          setMessage({
            text: `This ${actionLabel} has not started yet.`,
            type: "info",
          });
        }
      } else {
        console.log("[EventAction] Configuration response not successful");
      }
    } catch (err) {
      if (err.name === 'AbortError') throw err;
      console.error("[EventAction] Error loading configuration:", err);
      if (err.response?.status === 404) {
        setMessage({
          text: `No active ${actionLabel} found for this item.`,
          type: "error",
        });
      }
    }
  };

  /**
   * Parse attributes from various formats
   * Handles: arrays of attribute objects, JSON strings, plain objects
   */
  const parseAttributes = (attrs) => {
    if (!attrs) return {};

    // Handle array format (from database)
    if (Array.isArray(attrs)) {
      const grouped = {};
      
      for (const row of attrs) {
        const attrName = row?.attributename || row?.attribute_name || row?.name || row?.attribute;
        const attrValue = row?.attributevalue || row?.attribute_value || row?.value || row?.val;
        
        if (!attrName || !attrValue) continue;
        
        const name = String(attrName).trim();
        const value = String(attrValue).trim();
        
        if (!grouped[name]) {
          grouped[name] = new Set();
        }
        grouped[name].add(value);
      }
      
      const result = {};
      for (const [name, valueSet] of Object.entries(grouped)) {
        result[name] = Array.from(valueSet).sort();
      }
      
      return result;
    }

    // Handle JSON string
    let parsed = attrs;
    if (typeof attrs === "string") {
      try {
        parsed = JSON.parse(attrs);
      } catch {
        return {};
      }
    }

    // Handle object format
    if (typeof parsed === "object" && !Array.isArray(parsed)) {
      const result = {};
      for (const [key, value] of Object.entries(parsed)) {
        if (!value) continue;
        
        if (Array.isArray(value)) {
          result[key] = value.map(v => String(v).trim()).filter(Boolean).sort();
        } else if (typeof value === 'string') {
          const split = value.split(/[,|]/).map(x => x.trim()).filter(Boolean);
          result[key] = split.length > 1 ? split.sort() : [value.trim()];
        } else {
          result[key] = [String(value).trim()];
        }
      }
      return result;
    }

    return {};
  };

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Handle attribute selection in raffle
  const handleAttributeSelect = (attrName, attrValue) => {
    setFormData((prev) => ({
      ...prev,
      selectedAttributes: {
        ...prev.selectedAttributes,
        [attrName]: attrValue,
      },
    }));
  };

  /**
   * Validate form data before submission
   * Returns array of error messages (empty if valid)
   */
  const validateForm = () => {
    const errors = [];

    if (!formData.name.trim()) {
      errors.push("Name is required");
    }

    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.push("Valid email is required");
    }

    if (actionType === "auction") {
      if (!formData.bidamount || Number(formData.bidamount) <= 0) {
        errors.push("Valid bid amount is required");
      }
      if (configData?.starting_bid && Number(formData.bidamount) < Number(configData.starting_bid)) {
        errors.push(`Bid must be at least $${Number(configData.starting_bid).toFixed(2)}`);
      }
    }

    if (actionType === "challenge") {
      if (!formData.submission_url && !formData.submission_text) {
        errors.push("Please provide a submission URL or text");
      }
    }

    if (actionType === "ugc_campaign") {
      if (!formData.content_url && !formData.content_text) {
        errors.push("Please provide a content URL or description");
      }
    }

    if (actionType === "themed_day") {
      if (!formData.post_url && !formData.post_text) {
        errors.push("Please provide a post URL or text");
      }
    }

    if (actionType === "poll_quiz" && configData?.questions) {
      let questions = configData.questions;
      if (typeof questions === "string") {
        try { questions = JSON.parse(questions); } catch { questions = []; }
      }
      if (Array.isArray(questions) && questions.length > 0) {
        const answered = Object.keys(formData.responses).length;
        if (answered < questions.length) {
          errors.push("Please answer all questions");
        }
      }
    }

    // Require attributes if item has them (sizes for clothing/shoes, options for raffles)
    const hasAttributes = Object.keys(itemAttributes).length > 0;
    if (hasAttributes && Object.keys(formData.selectedAttributes).length === 0) {
      const isClothingOrShoes = itemInfo?.type === "clothing" || itemInfo?.type === "shoes";
      errors.push(isClothingOrShoes ? "Please select your size" : "Please select at least one attribute");
    }

    return errors;
  };

  /**
   * Handle form submission
   * Validates data and sends to appropriate endpoint
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check if action is active
    if (!configData || configData.hasEnded) {
      setMessage({
        text: `This ${actionLabel} has ended and is no longer accepting ${actionType === "auction" ? "bids" : "entries"}.`,
        type: "error",
      });
      return;
    }

    if (!configData.hasStarted) {
      setMessage({
        text: `This ${actionLabel} has not started yet. Please wait until ${new Date(configData.start_at).toLocaleString()}.`,
        type: "error",
      });
      return;
    }

    // Validate form data
    const errors = validateForm();
    if (errors.length > 0) {
      setMessage({
        text: errors.join(". "),
        type: "error",
      });
      return;
    }

    setSubmitting(true);
    setMessage({ text: "", type: "" });

    try {
      const endpointMap = {
        auction: `${baseUrl}/api/auction/${tenantId}/event/${eventId}/item/${itemId}/enter`,
        raffle: `${baseUrl}/api/raffle/${tenantId}/event/${eventId}/item/${itemId}/entry`,
        challenge: `${baseUrl}/api/challenge/${tenantId}/event/${eventId}/item/${itemId}/enter`,
        contest: `${baseUrl}/api/contest/${tenantId}/event/${eventId}/item/${itemId}/enter`,
        live_qna: formData.question
          ? `${baseUrl}/api/live-qna/${tenantId}/event/${eventId}/item/${itemId}/question`
          : `${baseUrl}/api/live-qna/${tenantId}/event/${eventId}/item/${itemId}/register`,
        ama: formData.question
          ? `${baseUrl}/api/ama/${tenantId}/event/${eventId}/item/${itemId}/question`
          : `${baseUrl}/api/ama/${tenantId}/event/${eventId}/item/${itemId}/register`,
        watch_party: `${baseUrl}/api/watch-party/${tenantId}/event/${eventId}/item/${itemId}/register`,
        workshop: `${baseUrl}/api/workshop/${tenantId}/event/${eventId}/item/${itemId}/register`,
        ugc_campaign: `${baseUrl}/api/ugc-campaign/${tenantId}/event/${eventId}/item/${itemId}/submit`,
        themed_day: `${baseUrl}/api/themed-day/${tenantId}/event/${eventId}/item/${itemId}/post`,
        collab_takeover: null, // Admin-only, no public submission
        poll_quiz: `${baseUrl}/api/poll-quiz/${tenantId}/event/${eventId}/item/${itemId}/response`,
      };

      const endpoint = endpointMap[actionType] || endpointMap.raffle;

      // Prepare payload
      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        phone: formData.phone.trim() || null,
      };

      if (actionType === "auction") {
        payload.bidamount = Number(formData.bidamount);
        payload.attributes = formData.selectedAttributes;
      } else if (actionType === "challenge") {
        payload.submission_url = formData.submission_url.trim() || null;
        payload.submission_text = formData.submission_text.trim() || null;
      } else if (actionType === "contest") {
        // Contest entries only need name/email/phone (already set above)
      } else if ((actionType === "live_qna" || actionType === "ama") && formData.question) {
        payload.question = formData.question.trim();
      } else if (actionType === "ugc_campaign") {
        payload.content_url = formData.content_url.trim() || null;
        payload.content_text = formData.content_text.trim() || null;
        payload.platform = formData.platform.trim() || null;
      } else if (actionType === "themed_day") {
        payload.post_url = formData.post_url.trim() || null;
        payload.post_text = formData.post_text.trim() || null;
        payload.platform = formData.platform.trim() || null;
      } else if (actionType === "poll_quiz") {
        payload.responses = formData.responses;
      } else if (actionType === "raffle") {
        payload.attributes = formData.selectedAttributes;
      }

      // Include selected attributes (sizes) for all action types when present
      if (Object.keys(formData.selectedAttributes).length > 0 && !payload.attributes) {
        payload.attributes = formData.selectedAttributes;
      }

      console.log("[EventAction] Submitting:", payload);
      const response = await axios.post(endpoint, payload);

      if (response.data.success) {
        const successMessages = {
          auction: `Bid submitted successfully! You bid $${Number(formData.bidamount).toFixed(2)}`,
          raffle: "Entry submitted successfully! Good luck!",
          challenge: "Challenge entry submitted successfully! Good luck!",
          contest: "Contest entry submitted successfully! Good luck!",
          live_qna: formData.question ? "Question submitted successfully!" : "Registration confirmed! You're signed up.",
          ama: formData.question ? "Question submitted successfully!" : "Registration confirmed! You're signed up.",
          watch_party: "Registration confirmed! See you at the watch party!",
          workshop: "Registration confirmed! You're signed up for the workshop.",
          ugc_campaign: "Content submitted successfully!",
          themed_day: "Post submitted successfully!",
          poll_quiz: "Response submitted successfully!",
        };

        setMessage({
          text: successMessages[actionType] || "Entry submitted successfully!",
          type: "success",
        });

        // Reset form
        setFormData({
          name: "",
          email: "",
          phone: "",
          bidamount: configData?.starting_bid?.toString() || "",
          selectedAttributes: {},
          submission_url: "",
          submission_text: "",
          question: "",
          content_url: "",
          content_text: "",
          post_url: "",
          post_text: "",
          platform: "",
          responses: {},
        });

        // Scroll to top to show success message
        window.scrollTo({ top: 0, behavior: "smooth" });

        // Refresh AMA Q&A feed after submission
        if (actionType === "ama") {
          loadAmaQuestions();
        }
      }
    } catch (err) {
      console.error("[EventAction] Error submitting:", err);
      setMessage({
        text: err.response?.data?.message || `Failed to submit ${actionLabel} entry`,
        type: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "Not set";
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Load AMA questions for the public Q&A feed
  const loadAmaQuestions = async () => {
    if (actionType !== "ama") return;
    setQuestionsLoading(true);
    try {
      const response = await axios.get(
        `${baseUrl}/api/ama/${tenantId}/event/${eventId}/item/${itemId}/questions`
      );
      if (response.data.success) {
        setAmaQuestions(response.data.questions || []);
      }
    } catch (err) {
      console.error("[EventAction] Error loading AMA questions:", err);
    } finally {
      setQuestionsLoading(false);
    }
  };

  // Load AMA questions on mount and when config loads
  useEffect(() => {
    if (actionType === "ama" && configData) {
      loadAmaQuestions();
    }
  }, [actionType, configData]);

  // Handle upvote
  const handleUpvote = async (questionId) => {
    if (upvotedIds.includes(questionId)) return;
    try {
      const response = await axios.post(
        `${baseUrl}/api/ama/${tenantId}/event/${eventId}/item/${itemId}/question/${questionId}/upvote`
      );
      if (response.data.success) {
        const newUpvotedIds = [...upvotedIds, questionId];
        setUpvotedIds(newUpvotedIds);
        localStorage.setItem(
          `ama_upvotes_${tenantId}_${eventId}_${itemId}`,
          JSON.stringify(newUpvotedIds)
        );
        setAmaQuestions((prev) =>
          prev.map((q) =>
            q.id === questionId ? { ...q, upvotes: response.data.upvotes } : q
          )
        );
      }
    } catch (err) {
      console.error("[EventAction] Error upvoting:", err);
    }
  };

  // Sort AMA questions
  const getSortedQuestions = () => {
    const sorted = [...amaQuestions];
    if (sortBy === "upvotes") {
      sorted.sort((a, b) => (b.upvotes || 0) - (a.upvotes || 0));
    } else if (sortBy === "newest") {
      sorted.sort((a, b) => new Date(b.created_on) - new Date(a.created_on));
    } else if (sortBy === "answered") {
      sorted.sort((a, b) => (b.answered ? 1 : 0) - (a.answered ? 1 : 0));
    }
    return sorted;
  };

  // Check if auction/raffle is currently active
  const isActive = () => {
    if (!configData) return false;
    return configData.hasStarted && !configData.hasEnded;
  };

  // Loading state with proper container
  if (loading) {
    return (
      <div className="event-action-container">
        <div className="loading">Loading {actionType} details</div>
      </div>
    );
  }

  // Get hero image URL from item media
  const heroMedia = itemMedia.length > 0 ? itemMedia[0] : null;

  // No config found state
  if (!configData) {
    return (
      <div className="event-action-container">
        {/* Hero image */}
        {heroMedia && (
          <div className="hero-image-container">
            {heroMedia.isVideo ? (
              <video
                className="hero-image"
                src={heroMedia.url}
                controls
                preload="metadata"
                playsInline
              />
            ) : (
              <img
                className="hero-image"
                src={heroMedia.url}
                alt={itemInfo?.name || actionLabel}
                onError={(e) => {
                  e.currentTarget.src = toAbs(baseUrl, "/public/app/default-image.png");
                }}
              />
            )}
          </div>
        )}

        <div className="dark-section">
          <div className="message message-error">
            No active {actionLabel.toLowerCase()} found for this item. Please check the link or contact the organizer.
          </div>
        </div>

        <div className="powered-by-footer">
          <a href="https://on-screen.app/download" target="_blank" rel="noopener noreferrer">
            Powered by On-Screen
          </a>
        </div>
      </div>
    );
  }

  // Main render
  return (
    <div className="event-action-container">
      {/* Hero image - tenant-provided image replaces logo/banner/item-info/media */}
      {heroMedia && (
        <div className="hero-image-container">
          {heroMedia.isVideo ? (
            <video
              className="hero-image"
              src={heroMedia.url}
              controls
              preload="metadata"
              playsInline
            />
          ) : (
            <img
              className="hero-image"
              src={heroMedia.url}
              alt={itemInfo?.name || actionLabel}
              onError={(e) => {
                e.currentTarget.src = toAbs(baseUrl, "/public/app/default-image.png");
              }}
            />
          )}
        </div>
      )}

      {/* Dark-themed bottom section */}
      <div className="dark-section">

      {/* Status messages */}
      {message.text && (
        <div className={`message message-${message.type}`}>{message.text}</div>
      )}

      {/* Configuration details */}
      {configData && (
        <div className="config-info">
          <h3>{actionLabel} Details</h3>
          <div className="info-grid">
            {actionType === "auction" && configData.starting_bid && (
              <div className="info-item">
                <span className="info-label">Starting Bid:</span>
                <span className="info-value">${Number(configData.starting_bid).toFixed(2)}</span>
              </div>
            )}
            {(actionType === "auction" || actionType === "challenge" || actionType === "contest") && configData.winners_to_pick && (
              <div className="info-item">
                <span className="info-label">Number of Winners:</span>
                <span className="info-value">{configData.winners_to_pick}</span>
              </div>
            )}
            {/* Challenge-specific: hashtag */}
            {actionType === "challenge" && configData.hashtag && (
              <div className="info-item">
                <span className="info-label">Hashtag:</span>
                <span className="info-value">{configData.hashtag}</span>
              </div>
            )}
            {/* Challenge/Contest description */}
            {(actionType === "challenge" || actionType === "contest") && configData.description && (
              <div className="info-item" style={{ gridColumn: "1 / -1" }}>
                <span className="info-label">Description:</span>
                <span className="info-value">{configData.description}</span>
              </div>
            )}
            {/* Prize details */}
            {(actionType === "challenge" || actionType === "contest") && configData.prize_details && (
              <div className="info-item" style={{ gridColumn: "1 / -1" }}>
                <span className="info-label">Prize:</span>
                <span className="info-value">{configData.prize_details}</span>
              </div>
            )}
            {/* Contest entry requirements */}
            {actionType === "contest" && configData.entry_requirements && (
              <div className="info-item" style={{ gridColumn: "1 / -1" }}>
                <span className="info-label">How to Enter:</span>
                <span className="info-value">{configData.entry_requirements}</span>
              </div>
            )}
            {/* Scheduled event details */}
            {["live_qna", "ama", "watch_party", "workshop"].includes(actionType) && configData.description && (
              <div className="info-item" style={{ gridColumn: "1 / -1" }}>
                <span className="info-label">Description:</span>
                <span className="info-value">{configData.description}</span>
              </div>
            )}
            {(actionType === "live_qna" || actionType === "ama") && configData.host_name && (
              <div className="info-item">
                <span className="info-label">Host:</span>
                <span className="info-value">{configData.host_name}</span>
              </div>
            )}
            {actionType === "ama" && configData.host_bio && (
              <div className="info-item" style={{ gridColumn: "1 / -1" }}>
                <span className="info-label">About the Host:</span>
                <span className="info-value">{configData.host_bio}</span>
              </div>
            )}
            {actionType === "workshop" && configData.instructor_name && (
              <div className="info-item">
                <span className="info-label">Instructor:</span>
                <span className="info-value">{configData.instructor_name}</span>
              </div>
            )}
            {["live_qna", "ama", "watch_party", "workshop"].includes(actionType) && configData.platform && (
              <div className="info-item">
                <span className="info-label">Platform:</span>
                <span className="info-value">{configData.platform}</span>
              </div>
            )}
            {configData.scheduled_at && (
              <div className="info-item">
                <span className="info-label">Scheduled:</span>
                <span className="info-value">{formatDate(configData.scheduled_at)}</span>
              </div>
            )}
            {configData.duration_minutes && (
              <div className="info-item">
                <span className="info-label">Duration:</span>
                <span className="info-value">{configData.duration_minutes} minutes</span>
              </div>
            )}
            {actionType === "workshop" && configData.max_participants > 0 && (
              <div className="info-item">
                <span className="info-label">Capacity:</span>
                <span className="info-value">Max {configData.max_participants} participants</span>
              </div>
            )}
            {/* UGC Campaign details */}
            {actionType === "ugc_campaign" && configData.description && (
              <div className="info-item" style={{ gridColumn: "1 / -1" }}>
                <span className="info-label">Description:</span>
                <span className="info-value">{configData.description}</span>
              </div>
            )}
            {actionType === "ugc_campaign" && configData.hashtag && (
              <div className="info-item">
                <span className="info-label">Hashtag:</span>
                <span className="info-value">{configData.hashtag}</span>
              </div>
            )}
            {actionType === "ugc_campaign" && configData.content_guidelines && (
              <div className="info-item" style={{ gridColumn: "1 / -1" }}>
                <span className="info-label">Content Guidelines:</span>
                <span className="info-value">{configData.content_guidelines}</span>
              </div>
            )}
            {/* Themed Day details */}
            {actionType === "themed_day" && configData.theme_description && (
              <div className="info-item" style={{ gridColumn: "1 / -1" }}>
                <span className="info-label">Theme:</span>
                <span className="info-value">{configData.theme_description}</span>
              </div>
            )}
            {actionType === "themed_day" && configData.hashtag && (
              <div className="info-item">
                <span className="info-label">Hashtag:</span>
                <span className="info-value">{configData.hashtag}</span>
              </div>
            )}
            {actionType === "themed_day" && configData.post_guidelines && (
              <div className="info-item" style={{ gridColumn: "1 / -1" }}>
                <span className="info-label">Post Guidelines:</span>
                <span className="info-value">{configData.post_guidelines}</span>
              </div>
            )}
            {/* Collab/Takeover details */}
            {actionType === "collab_takeover" && configData.description && (
              <div className="info-item" style={{ gridColumn: "1 / -1" }}>
                <span className="info-label">Description:</span>
                <span className="info-value">{configData.description}</span>
              </div>
            )}
            {actionType === "collab_takeover" && configData.partner_name && (
              <div className="info-item">
                <span className="info-label">Partner:</span>
                <span className="info-value">{configData.partner_name}</span>
              </div>
            )}
            {actionType === "collab_takeover" && configData.duration_hours && (
              <div className="info-item">
                <span className="info-label">Duration:</span>
                <span className="info-value">{configData.duration_hours} hours</span>
              </div>
            )}
            {/* Poll/Quiz details */}
            {actionType === "poll_quiz" && configData.description && (
              <div className="info-item" style={{ gridColumn: "1 / -1" }}>
                <span className="info-label">Description:</span>
                <span className="info-value">{configData.description}</span>
              </div>
            )}
            {actionType === "poll_quiz" && (
              <div className="info-item">
                <span className="info-label">Type:</span>
                <span className="info-value">{configData.type === "QUIZ" ? "Quiz" : "Poll"}</span>
              </div>
            )}
            {configData.start_at && (
              <div className="info-item">
                <span className="info-label">Starts:</span>
                <span className="info-value">{formatDate(configData.start_at)}</span>
              </div>
            )}
            {configData.end_at && (
              <div className="info-item">
                <span className="info-label">Ends:</span>
                <span className="info-value">{formatDate(configData.end_at)}</span>
              </div>
            )}
            <div className="info-item">
              <span className="info-label">Status:</span>
              <span className={`info-value status-${configData.hasEnded ? 'ended' : configData.hasStarted ? 'active' : 'pending'}`}>
                {configData.hasEnded ? "Ended" : configData.hasStarted ? "Active" : "Not Started"}
              </span>
            </div>
          </div>

          {/* Bid statistics for auctions */}
          {actionType === "auction" && configData.stats && (
            <div className="bid-stats">
              <h4>Current Bidding</h4>
              <div className="stats-row">
                <div className="stat-box">
                  <div className="stat-label">Total Bids</div>
                  <div className="stat-value">{configData.stats.entry_count || 0}</div>
                </div>
                {configData.stats.highest_bid && (
                  <div className="stat-box">
                    <div className="stat-label">Highest Bid</div>
                    <div className="stat-value highlight">
                      ${Number(configData.stats.highest_bid).toFixed(2)}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Collab/Takeover is admin-managed — no public form */}
      {actionType === "collab_takeover" && configData && (
        <div className="inactive-notice">
          This {configData.type === "TAKEOVER" ? "takeover" : "collaboration"} is managed by the organizer. Stay tuned for updates!
        </div>
      )}

      {/* Entry form - only shown if action is active and has a public form */}
      {isActive() && actionType !== "collab_takeover" && (
        <form onSubmit={handleSubmit} className="entry-form">
          <h3>
            {actionType === "auction" ? "Place Your Bid"
              : actionType === "challenge" ? "Submit Your Entry"
              : actionType === "contest" ? "Enter Contest"
              : actionType === "live_qna" ? "Register / Ask a Question"
              : actionType === "ama" ? "Register / Ask a Question"
              : actionType === "watch_party" ? "Register for Watch Party"
              : actionType === "workshop" ? "Register for Workshop"
              : actionType === "ugc_campaign" ? "Submit Your Content"
              : actionType === "themed_day" ? "Share Your Post"
              : actionType === "poll_quiz" ? (configData?.type === "QUIZ" ? "Take the Quiz" : "Vote in the Poll")
              : "Enter Raffle"}
          </h3>

          {/* Name field */}
          <div className="form-group">
            <label htmlFor="name">Name *</label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              placeholder="Your full name"
              required
              disabled={submitting}
            />
          </div>

          {/* Email field */}
          <div className="form-group">
            <label htmlFor="email">Email *</label>
            <input
              type="email"
              id="email"
              value={formData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              placeholder="your.email@example.com"
              required
              disabled={submitting}
            />
          </div>

          {/* Phone field (optional) */}
          <div className="form-group">
            <label htmlFor="phone">Phone (optional)</label>
            <input
              type="tel"
              id="phone"
              value={formData.phone}
              onChange={(e) => handleInputChange("phone", e.target.value)}
              placeholder="(555) 123-4567"
              disabled={submitting}
            />
          </div>

          {/* Challenge submission fields */}
          {actionType === "challenge" && (
            <>
              <div className="form-group">
                <label htmlFor="submission_url">Submission URL</label>
                <input
                  type="url"
                  id="submission_url"
                  value={formData.submission_url}
                  onChange={(e) => handleInputChange("submission_url", e.target.value)}
                  placeholder="https://link-to-your-submission.com"
                  disabled={submitting}
                />
                <span className="form-hint">Link to your challenge submission (video, photo, post, etc.)</span>
              </div>
              <div className="form-group">
                <label htmlFor="submission_text">
                  Submission Description {!formData.submission_url && "*"}
                </label>
                <textarea
                  id="submission_text"
                  value={formData.submission_text}
                  onChange={(e) => handleInputChange("submission_text", e.target.value)}
                  placeholder="Describe your submission or paste content here"
                  rows={3}
                  disabled={submitting}
                  required={!formData.submission_url}
                />
              </div>
            </>
          )}

          {/* Q&A question field (live_qna/ama) */}
          {(actionType === "live_qna" || actionType === "ama") && (
            <div className="form-group">
              <label htmlFor="question">
                Your Question (optional)
              </label>
              <textarea
                id="question"
                value={formData.question}
                onChange={(e) => handleInputChange("question", e.target.value)}
                placeholder="Ask a question (or leave blank to just register)"
                rows={3}
                disabled={submitting}
              />
              <span className="form-hint">
                Submit a question or just register with your name and email above
              </span>
            </div>
          )}

          {/* UGC Campaign submission fields */}
          {actionType === "ugc_campaign" && (
            <>
              <div className="form-group">
                <label htmlFor="content_url">Content URL</label>
                <input
                  type="url"
                  id="content_url"
                  value={formData.content_url}
                  onChange={(e) => handleInputChange("content_url", e.target.value)}
                  placeholder="https://link-to-your-content.com"
                  disabled={submitting}
                />
                <span className="form-hint">Link to your content (video, photo, post, etc.)</span>
              </div>
              <div className="form-group">
                <label htmlFor="content_text">
                  Content Description {!formData.content_url && "*"}
                </label>
                <textarea
                  id="content_text"
                  value={formData.content_text}
                  onChange={(e) => handleInputChange("content_text", e.target.value)}
                  placeholder="Describe your content or paste text here"
                  rows={3}
                  disabled={submitting}
                  required={!formData.content_url}
                />
              </div>
              <div className="form-group">
                <label htmlFor="platform">Platform (optional)</label>
                <input
                  type="text"
                  id="platform"
                  value={formData.platform}
                  onChange={(e) => handleInputChange("platform", e.target.value)}
                  placeholder="e.g. Instagram, TikTok, YouTube"
                  disabled={submitting}
                />
              </div>
            </>
          )}

          {/* Themed Day post fields */}
          {actionType === "themed_day" && (
            <>
              <div className="form-group">
                <label htmlFor="post_url">Post URL</label>
                <input
                  type="url"
                  id="post_url"
                  value={formData.post_url}
                  onChange={(e) => handleInputChange("post_url", e.target.value)}
                  placeholder="https://link-to-your-post.com"
                  disabled={submitting}
                />
                <span className="form-hint">Link to your themed post</span>
              </div>
              <div className="form-group">
                <label htmlFor="post_text">
                  Post Text {!formData.post_url && "*"}
                </label>
                <textarea
                  id="post_text"
                  value={formData.post_text}
                  onChange={(e) => handleInputChange("post_text", e.target.value)}
                  placeholder="Write your themed post here"
                  rows={3}
                  disabled={submitting}
                  required={!formData.post_url}
                />
              </div>
              <div className="form-group">
                <label htmlFor="platform">Platform (optional)</label>
                <input
                  type="text"
                  id="platform"
                  value={formData.platform}
                  onChange={(e) => handleInputChange("platform", e.target.value)}
                  placeholder="e.g. Instagram, Twitter, TikTok"
                  disabled={submitting}
                />
              </div>
            </>
          )}

          {/* Poll/Quiz response fields */}
          {actionType === "poll_quiz" && configData?.questions && (() => {
            let questions = configData.questions;
            if (typeof questions === "string") {
              try { questions = JSON.parse(questions); } catch { questions = []; }
            }
            if (!Array.isArray(questions)) questions = [];
            return questions.length > 0 && (
              <div className="poll-quiz-questions">
                {questions.map((q, qIndex) => (
                  <div key={qIndex} className="form-group">
                    <label>{q.question || `Question ${qIndex + 1}`} *</label>
                    {(q.options || []).map((opt, oIndex) => (
                      <label key={oIndex} className="radio-label">
                        <input
                          type="radio"
                          name={`question_${qIndex}`}
                          value={oIndex}
                          checked={formData.responses[qIndex] === oIndex}
                          onChange={() => handleInputChange("responses", {
                            ...formData.responses,
                            [qIndex]: oIndex,
                          })}
                          disabled={submitting}
                        />
                        {opt}
                      </label>
                    ))}
                  </div>
                ))}
              </div>
            );
          })()}

          {/* Bid amount field (auction only) */}
          {actionType === "auction" && (
            <div className="form-group">
              <label htmlFor="bidamount">
                Bid Amount * 
                {configData?.starting_bid && (
                  <span className="min-bid"> (Minimum: ${Number(configData.starting_bid).toFixed(2)})</span>
                )}
              </label>
              <div className="bid-input-wrapper">
                <span className="currency-symbol">$</span>
                <input
                  type="number"
                  id="bidamount"
                  value={formData.bidamount}
                  onChange={(e) => handleInputChange("bidamount", e.target.value)}
                  placeholder="0.00"
                  min={configData?.starting_bid || "0"}
                  step="1"
                  required
                  disabled={submitting}
                />
              </div>
            </div>
          )}

          {/* Attribute/size selection - shown for clothing/shoe items on all action types, and for raffles */}
          {Object.keys(itemAttributes).length > 0 && (
            <div className="attributes-section">
              <h4>{itemInfo?.type === "clothing" || itemInfo?.type === "shoes" ? "Select Your Size" : "Select Your Options"}</h4>
              {Object.entries(itemAttributes).map(([attrName, attrValues]) => (
                <div key={attrName} className="form-group">
                  <label>{attrName} *</label>
                  <select
                    value={formData.selectedAttributes[attrName] || ""}
                    onChange={(e) => handleAttributeSelect(attrName, e.target.value)}
                    required
                    disabled={submitting}
                  >
                    <option value="">-- Select {attrName} --</option>
                    {attrValues.map((val) => (
                      <option key={val} value={val}>
                        {val}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          )}

          {/* Submit button */}
          <button type="submit" className="submit-button" disabled={submitting}>
            {submitting
              ? "Submitting..."
              : actionType === "auction" ? "Place Bid"
              : actionType === "challenge" ? "Submit Entry"
              : actionType === "contest" ? "Enter Contest"
              : actionType === "live_qna" ? (formData.question ? "Submit Question" : "Register")
              : actionType === "ama" ? (formData.question ? "Submit Question" : "Register")
              : actionType === "watch_party" ? "Register"
              : actionType === "workshop" ? "Register"
              : actionType === "ugc_campaign" ? "Submit Content"
              : actionType === "themed_day" ? "Share Post"
              : actionType === "poll_quiz" ? "Submit Response"
              : "Enter Raffle"}
          </button>
        </form>
      )}

      {/* AMA Q&A Feed - public view of questions and answers */}
      {actionType === "ama" && amaQuestions.length > 0 && (
        <div className="qa-feed-section">
          <div className="qa-feed-header">
            <h3>Questions & Answers ({amaQuestions.length})</h3>
            <div className="qa-sort-controls">
              {[
                { key: "upvotes", label: "Top" },
                { key: "newest", label: "New" },
                { key: "answered", label: "Answered" },
              ].map((s) => (
                <button
                  key={s.key}
                  type="button"
                  className={`qa-sort-btn${sortBy === s.key ? " active" : ""}`}
                  onClick={() => setSortBy(s.key)}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {questionsLoading ? (
            <div className="loading">Loading questions...</div>
          ) : (
            getSortedQuestions().map((q) => (
              <div key={q.id} className={`qa-card${q.answered ? " qa-card-answered" : ""}`}>
                <div className="qa-card-main">
                  <div className="qa-upvote-col">
                    <button
                      type="button"
                      className={`qa-upvote-btn${upvotedIds.includes(q.id) ? " upvoted" : ""}`}
                      onClick={() => handleUpvote(q.id)}
                      disabled={upvotedIds.includes(q.id)}
                      title={upvotedIds.includes(q.id) ? "Already upvoted" : "Upvote this question"}
                    >
                      &#9650;
                    </button>
                    <span className="qa-upvote-count">{q.upvotes || 0}</span>
                  </div>
                  <div className="qa-content-col">
                    <p className="qa-question-text">{q.question}</p>
                    <div className="qa-meta">
                      <span className="qa-asker">{q.name}</span>
                      <span className="qa-date">{new Date(q.created_on).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>

                {q.answered && q.answer && (
                  <div className="qa-answer">
                    <div className="qa-answer-badge">Answer</div>
                    <p className="qa-answer-text">{q.answer}</p>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* Inactive notice - shown when not active (skip for collab_takeover which has its own notice) */}
      {!isActive() && configData && actionType !== "collab_takeover" && (
        <div className="inactive-notice">
          {configData.hasEnded
            ? `This ${actionLabel.toLowerCase()} has ended. Thank you for your interest!`
            : `This ${actionLabel.toLowerCase()} will begin on ${formatDate(configData.start_at)}`
          }
        </div>
      )}

      </div>{/* end dark-section */}

      <div className="powered-by-footer">
        <a href="https://on-screen.app/download" target="_blank" rel="noopener noreferrer">
          Powered by On-Screen
        </a>
      </div>
    </div>
  );
};

export default EventAction;